import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setGlobalUnauthorizedHandler } from '../config/api';
import { useToaster } from './ToasterContext';
import { authenticatedApi } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showWarning } = useToaster();

  // Handle unauthorized responses (401)
  const handleUnauthorized = () => {
    
    // Show session expired notification
    showWarning(
      'Session Expired', 
      'Your session has expired. Please login again.'
    );
    
    logout();
  };

  // Initialize user from localStorage and check for tokens
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for tokens in localStorage first
        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        
        // Check for user data in both localStorage and sessionStorage (for backward compatibility)
        let savedUser = localStorage.getItem('inchaa_user') || sessionStorage.getItem('inchaa_user');
        
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            // Check if user status is active
            if (userData.status === 'active' && accessToken) {
              // User data exists and tokens are present - restore session
              setUser(userData);
              // Migrate user data to localStorage if it was in sessionStorage
              if (!localStorage.getItem('inchaa_user')) {
                localStorage.setItem('inchaa_user', JSON.stringify(userData));
                sessionStorage.removeItem('inchaa_user');
              }
              setLoading(false);
              return;
            } else {
              // Clear invalid user data
              localStorage.removeItem('inchaa_user');
              sessionStorage.removeItem('inchaa_user');
            }
          } catch (error) {
            console.error('Error parsing user data:', error);
            localStorage.removeItem('inchaa_user');
            sessionStorage.removeItem('inchaa_user');
          }
        }
        
        // If tokens exist but no user data, try to fetch user profile
        if (accessToken && refreshToken && !savedUser) {
          try {
            const profileResponse = await authenticatedApi.getUserProfile();
            if (profileResponse && profileResponse.result) {
              const userData = profileResponse.result;
              // Construct user object similar to login response
              const user = {
                id: userData.id || userData.userId,
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'User',
                email: userData.email,
                role: userData.userType || userData.role || '',
                status: userData.status === 'active' ? 'active' : userData.status,
                preferredLanguage: userData.preferredLanguage || 'en',
                contractorRoles: userData.roles || userData.contractorRoles || [],
              };
              
              if (user.status === 'active') {
                setUser(user);
                localStorage.setItem('inchaa_user', JSON.stringify(user));
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
            // If profile fetch fails (e.g., token expired), clear tokens
            if (error?.response?.status === 401) {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            }
          }
        }
        
        // No valid session found
        setLoading(false);
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, []);

  // Set up global unauthorized handler when component mounts
  useEffect(() => {
    setGlobalUnauthorizedHandler(handleUnauthorized);
  }, []);

  // Listen for storage changes to sync authentication across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Only handle changes to our authentication keys
      if (e.key === 'access_token' || e.key === 'refresh_token' || e.key === 'inchaa_user') {
        // If tokens or user data were removed, logout
        if (e.key === 'access_token' && !e.newValue) {
          setUser(null);
          localStorage.removeItem('inchaa_user');
          sessionStorage.removeItem('inchaa_user');
        } else if (e.key === 'inchaa_user') {
          // User data changed - update user state
          if (e.newValue) {
            try {
              const userData = JSON.parse(e.newValue);
              if (userData.status === 'active') {
                setUser(userData);
              }
            } catch (error) {
              console.error('Error parsing user data from storage event:', error);
            }
          } else {
            // User data was removed - logout
            setUser(null);
          }
        }
      }
    };

    // Listen for storage events (fired when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = (userData) => {
    // Check if user is approved
    if (userData.status !== 'active') {
      throw new Error('Account is not approved. Please wait for approval or contact support.');
    }
    
    // Validate that both tokens are present in localStorage
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!accessToken || !refreshToken) {
      console.error('AuthContext: Login failed - Missing tokens', { 
        accessToken: !!accessToken, 
        refreshToken: !!refreshToken 
      });
      throw new Error('Authentication tokens are missing. Please try logging in again.');
    }
    
    
    
    
    
    setUser(userData);
    // Store user data in localStorage for cross-tab persistence
    localStorage.setItem('inchaa_user', JSON.stringify(userData));
    // Also keep in sessionStorage for backward compatibility (will be migrated on next init)
    sessionStorage.setItem('inchaa_user', JSON.stringify(userData));
  };

  const logout = () => {
    // Clear all authentication data from both localStorage and sessionStorage
    localStorage.removeItem('inchaa_user');
    sessionStorage.removeItem('inchaa_user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    window.location.href = '/login';
  };

  const switchRole = (newRole) => {
    if (!user) return;
    
    const updatedUser = { ...user, role: newRole };
    setUser(updatedUser);
    // Update user data in both localStorage and sessionStorage
    localStorage.setItem('inchaa_user', JSON.stringify(updatedUser));
    sessionStorage.setItem('inchaa_user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!user && user.status === 'active';
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    switchRole,
    isAuthenticated,
    hasRole,
    hasAnyRole,
  };

  // Debug: Log user state changes
  useEffect(() => {
    
  }, [user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 